import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, CheckCircle, Clock, Download, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ContentExtrasBar } from '@/components/content/ContentExtrasBar';

interface ResourceContentModalProps {
  isOpen: boolean;
  contentId?: string;
  contentDescription?: string | null;
  onClose: () => void;
  lesson: {
    title: string;
    description: string;
    duration: string;
  };
  resourceUrl?: string | null;
  onComplete: () => void;
  isCompleted: boolean;
}

export function ResourceContentModal({
  isOpen,
  contentId,
  contentDescription,
  onClose,
  lesson,
  resourceUrl,
  onComplete,
  isCompleted,
}: ResourceContentModalProps) {
  const isPdf = resourceUrl?.toLowerCase().includes('.pdf') ?? false;
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleClose = () => {
    setIsFullscreen(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className={cn(
        "flex flex-col p-0 gap-0",
        isFullscreen
          ? "max-w-[100vw] w-screen h-screen max-h-screen rounded-none"
          : isPdf ? "max-w-5xl max-h-[95vh]" : "max-w-lg max-h-[90vh]"
      )}>
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="secondary" className="gap-1">
                <FileText className="w-3 h-3" />
                Recurso
              </Badge>
              <Badge variant="outline" className="gap-1">
                <Clock className="w-3 h-3" />
                {lesson.duration}
              </Badge>
              {isCompleted && (
                <Badge className="bg-success text-success-foreground gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Completado
                </Badge>
              )}
            </div>
            <button
              onClick={() => setIsFullscreen(f => !f)}
              className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
              title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4 text-muted-foreground" /> : <Maximize2 className="w-4 h-4 text-muted-foreground" />}
            </button>
          </div>
          <DialogTitle className="text-xl mt-2">{lesson.title}</DialogTitle>
          {lesson.description && (
            <p className="text-sm text-muted-foreground mt-1">{lesson.description}</p>
          )}
        </DialogHeader>
        {contentId && <ContentExtrasBar contentId={contentId} description={contentDescription ?? lesson?.description} />}

        {/* Content body */}
        <div className="flex-1 overflow-hidden">
          {isPdf && resourceUrl ? (
            <iframe
              src={resourceUrl}
              className="w-full border-0"
              style={{ height: isFullscreen ? 'calc(100vh - 180px)' : '65vh' }}
              title={lesson.title}
            />
          ) : resourceUrl ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 gap-6">
              <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center">
                <FileText className="w-10 h-10 text-muted-foreground" />
              </div>
              <div className="text-center space-y-2">
                <p className="font-medium text-foreground">{lesson.title}</p>
                <p className="text-sm text-muted-foreground">
                  Haz clic en el botón para descargar o abrir este recurso.
                </p>
              </div>
              <Button
                onClick={() => window.open(resourceUrl, '_blank')}
                className="gap-2"
                size="lg"
              >
                <Download className="w-5 h-5" />
                Descargar recurso
              </Button>
            </div>
          ) : (
            <div className="text-muted-foreground text-center py-12 px-6">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay recurso disponible para esta lección.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex-shrink-0 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {isCompleted ? 'Recurso completado' : 'Revisa el recurso y márcalo como completado'}
          </span>
          {!isCompleted ? (
            <Button onClick={onComplete} className="gap-2">
              <CheckCircle className="w-4 h-4" />
              Marcar como completado
            </Button>
          ) : (
            <Button variant="outline" onClick={handleClose}>
              Cerrar
            </Button>
          )}
        </div>
        </DialogContent>
    </Dialog>
  );
}